import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '../ui/drawer';
import {
  Repeat, Focus, Coffee, BarChart3, FileText, Bell,
  Users, Webhook, Mail, Trash2, HelpCircle, Shield,
  Settings, LogOut, Receipt, Globe, Check, ChevronRight, Bot, CalendarClock, Sparkles,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useRole } from '../../hooks/useRole';
import { useEntitlements } from '../../hooks/useEntitlements';
import { useMyBotsQuery } from '../../hooks/queries';
import { useLanguage } from '../../contexts/LanguageContext';
import { SUPPORTED_LANGUAGES } from '../../i18n/locales';
import { GOOGLE_APP_INTEGRATIONS } from '../../lib/constants';
import { App as CapApp } from '@capacitor/app';
import { isNative } from '../../native/platform';

// iOS Settings-style row. Always has a leading icon tile, label, optional
// trailing accessory (value text, badge, "PRO", checkmark) and a chevron when
// the row leads somewhere.
function Row({ to, icon: Icon, label, value, badge, badgeTone, gated, onClick, danger, showChevron = true, iconColor }) {
  const inner = (
    <>
      <span className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${iconColor || (danger ? 'bg-red-500/15' : 'bg-muted/70')}`}>
        <Icon className={`w-[16px] h-[16px] ${danger ? 'text-red-500' : 'text-foreground/85'}`} />
      </span>
      <span className={`flex-1 truncate text-[15px] ${danger ? 'text-red-500' : 'text-foreground'}`}>{label}</span>
      {gated && (
        <span className="text-[10px] uppercase tracking-wide font-bold text-amber-700 dark:text-amber-400">Pro</span>
      )}
      {!!badge && (
        <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold leading-5 text-center ${badgeTone === 'attention' ? 'bg-attention text-honey-ink' : 'bg-amber-500 text-honey-ink'}`}>
          {badge > 99 ? '99+' : badge}
        </span>
      )}
      {value && (
        <span className="text-[15px] text-muted-foreground truncate max-w-[140px]">{value}</span>
      )}
      {showChevron && !danger && (
        <ChevronRight className="w-4 h-4 text-muted-foreground/50 flex-shrink-0" />
      )}
    </>
  );

  // 44pt tap target, full-width hit area, hairline separator between rows in the same Group.
  const cls = 'flex items-center gap-3 px-4 min-h-[44px] py-2 transition-colors active:bg-foreground/5';
  if (to) {
    return <Link to={to} onClick={onClick} className={cls}>{inner}</Link>;
  }
  return <button type="button" onClick={onClick} className={`w-full text-left ${cls}`}>{inner}</button>;
}

// iOS-style grouped card. Hairline separators between rows are absolutely
// positioned via after:* so they don't add layout height.
function Group({ children, label }) {
  return (
    <div className="px-4">
      {label && (
        <p className="px-1 pb-2 text-[12px] uppercase tracking-wider text-muted-foreground/70 font-semibold">{label}</p>
      )}
      <div className="rounded-xl bg-card overflow-hidden border border-border/60 [&>*+*]:border-t [&>*+*]:border-border/40">
        {children}
      </div>
    </div>
  );
}

export default function MobileMoreSheet({ open, onOpenChange }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { isAdmin } = useRole();
  const entitlements = useEntitlements();
  const { has } = entitlements;
  const { language, setLanguage } = useLanguage();
  const { data: botsData } = useMyBotsQuery();
  const waitingForYou = (botsData?.bots || []).reduce((sum, b) => sum + (b.pending_approvals || 0), 0);

  const [showLangPicker, setShowLangPicker] = React.useState(false);
  const currentLanguageLabel = SUPPORTED_LANGUAGES.find(l => l.code === language)?.native || 'English';

  // Native app version from the store build (codemagic.yaml); the web has none.
  const [appInfo, setAppInfo] = React.useState(null);
  React.useEffect(() => {
    if (isNative()) CapApp.getInfo().then(setAppInfo).catch(() => {});
  }, []);

  const close = () => onOpenChange(false);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="bg-muted/40 dark:bg-card max-h-[92dvh]">
        <DrawerTitle className="sr-only">{t('common.more')}</DrawerTitle>

        <div className="overflow-y-auto pt-2 pb-[max(env(safe-area-inset-bottom),16px)] flex flex-col gap-4">
          {/* User card */}
          <div className="px-4">
            <Link
              to="/app/settings"
              onClick={close}
              className="flex items-center gap-3 p-3 rounded-xl bg-card border border-border/60 active:bg-foreground/5"
            >
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="" className="w-14 h-14 rounded-full object-cover" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-amber-500 flex items-center justify-center text-honey-ink font-semibold text-lg">
                  {(user?.full_name || t('common.user')).charAt(0)}
                </div>
              )}
              <div className="min-w-0 flex-1 text-left">
                <p className="text-foreground font-semibold text-[17px] truncate">{user?.full_name || t('common.user')}</p>
                <p className="text-muted-foreground text-[13px] truncate">{user?.email}</p>
                {user?.plan && (
                  <span className="inline-block mt-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-amber-500/15 text-amber-700 dark:text-amber-400">
                    {user.plan}
                    {user.subscription_status === 'trialing' && ` · ${t('nav.trial')}`}
                  </span>
                )}
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground/50 flex-shrink-0" />
            </Link>
          </div>

          {/* The bots: managed from here and from the chats (Tasks is a tab of its own) */}
          <Group>
            <Row to="/app/bots" icon={Bot} label={t('nav.manage_bots')} badge={waitingForYou} badgeTone="attention" onClick={close} iconColor="bg-amber-500/15 dark:bg-amber-500/10" />
            {(botsData?.bots || []).length > 0 && <Row to="/app/bots/scheduled" icon={CalendarClock} label={t('bots.scheduled.title')} onClick={close} />}
          </Group>

          {/* Productivity tools */}
          <Group>
            <Row to="/app/spotlight" icon={Sparkles} label={t('spotlight.title')} onClick={close} />
            <Row to="/app/habits" icon={Repeat} label={t('nav.habits')} onClick={close} />
            <Row to="/app/focus-time" icon={Focus} label={t('nav.focus_time')} onClick={close} />
            <Row to="/app/buffer-time" icon={Coffee} label={t('nav.buffer_time')} onClick={close} />
            <Row to="/app/analytics" icon={BarChart3} label={t('nav.analytics')} onClick={close} />
            <Row to="/app/documents" icon={FileText} label={t('nav.documents')} onClick={close} />
            <Row to="/app/notifications" icon={Bell} label={t('notifications.title')} onClick={close} />
          </Group>

          {/* Integrations / collaboration */}
          <Group>
            <Row to="/app/teams" icon={Users} label={t('nav.teams')} gated={!has('teams')} onClick={close} />
            {GOOGLE_APP_INTEGRATIONS && (
              <Row to="/app/gmail" icon={Mail} label={t('nav.email')} gated={!has('gmail')} onClick={close} />
            )}
            <Row to="/app/webhooks" icon={Webhook} label={t('nav.webhooks')} gated={!has('webhooks')} onClick={close} />
          </Group>

          {/* Account & app */}
          <Group>
            <Row to="/app/settings" icon={Settings} label={t('nav.settings')} onClick={close} />
            <Row to="/app/subscription" icon={Receipt} label={t('nav.billing')} onClick={close} />
            <Row to="/app/trash" icon={Trash2} label={t('nav.trash')} onClick={close} />
            <Row to="/app/help" icon={HelpCircle} label={t('nav.help')} onClick={close} />
            {isAdmin && <Row to="/app/admin" icon={Shield} label={t('nav.admin')} onClick={close} />}
            <Row
              icon={Globe}
              label={t('settings.language.title')}
              value={currentLanguageLabel}
              onClick={() => setShowLangPicker(true)}
            />
          </Group>

          {/* Destructive - alone, red */}
          <Group>
            <Row
              icon={LogOut}
              label={t('nav.logout')}
              danger
              showChevron={false}
              onClick={() => { close(); logout(); }}
            />
          </Group>

          {appInfo?.version && (
            <p className="px-4 text-center text-[12px] text-muted-foreground/70 select-text">
              {t('native.app_version', { version: appInfo.version, build: appInfo.build })}
            </p>
          )}
        </div>

        {showLangPicker && (
          <div className="fixed inset-0 z-[60] flex items-end" onClick={() => setShowLangPicker(false)}>
            <div className="absolute inset-0 bg-black/60" />
            <div
              className="relative w-full bg-card rounded-t-2xl max-h-[80dvh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            >
              <div className="sticky top-0 bg-card px-4 py-3 border-b border-border">
                <p className="font-semibold text-foreground text-[17px]">{t('settings.language.title')}</p>
              </div>
              <div className="[&>*+*]:border-t [&>*+*]:border-border/40">
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => { setLanguage(lang.code); setShowLangPicker(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3.5 text-foreground text-[15px] active:bg-foreground/5"
                  >
                    <span className="text-base leading-none w-6 text-center flex-shrink-0">{lang.flag}</span>
                    <span className="flex-1 min-w-0 text-left">
                      <span className="block truncate">{lang.native}</span>
                      <span className="block text-xs text-muted-foreground truncate">{lang.name}</span>
                    </span>
                    {language === lang.code && <Check className="w-5 h-5 text-amber-500 flex-shrink-0" />}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
}
